import { useState, useRef, useEffect } from "react";
import { Pencil, Trash2, Save, X } from "lucide-react";
import { Card, CardContent, CardFooter } from "@/modern/components/ui/card";
import { Button } from "@/modern/components/ui/button";
import { Textarea } from "@/modern/components/ui/textarea";
import { Switch } from "@/modern/components/ui/switch";
import { cn } from "@/modern/utils";

interface CardData {
  id: string;
  content: string;
  enabled: boolean;
  isDraft?: boolean;
}

interface SettingsCardProps {
  card: CardData;
  isEditMode: boolean;
  canDelete: boolean;
  canDisable: boolean;
  canEdit: boolean;
  onEdit: (id: string) => void;
  onSave: (id: string, data: { content: string; enabled: boolean }) => void;
  onCancel: (id: string) => void;
  onDelete: (id: string) => void;
}

const SettingsCard = ({ card, isEditMode, canDelete, canDisable, canEdit, onEdit, onSave, onCancel, onDelete }: SettingsCardProps) => {
  const [content, setContent] = useState(card.content);
  const [enabled, setEnabled] = useState(card.enabled);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-focus textarea when entering edit mode
  useEffect(() => {
    if (isEditMode && textareaRef.current) {
      textareaRef.current.focus();
      // Move cursor to end
      textareaRef.current.setSelectionRange(textareaRef.current.value.length, textareaRef.current.value.length);
    }
  }, [isEditMode]);

  // Sync with card prop when not in edit mode
  useEffect(() => {
    if (!isEditMode) {
      setContent(card.content);
      setEnabled(card.enabled);
    }
  }, [card, isEditMode]);

  const handleSave = () => {
    const trimmedContent = content.trim();
    if (!trimmedContent) {
      return; // Prevent saving empty content
    }
    onSave(card.id, { content: trimmedContent, enabled });
  };

  const handleCancel = () => {
    setContent(card.content);
    setEnabled(card.enabled);
    onCancel(card.id);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Escape") {
      handleCancel();
    } else if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      handleSave();
    }
  };

  const isValid = content.trim().length > 0;

  if (!isEditMode) {
    return (
      <Card className={cn("relative group transition-colors", canEdit && "cursor-pointer hover:bg-accent/50", !card.enabled && "opacity-60")} onClick={() => canEdit && onEdit(card.id)}>
        <CardContent className="p-4">
          <div className="absolute top-3 right-3 flex items-center gap-2">
            {!card.enabled && (
              <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700">
                Disabled
              </span>
            )}
            {card.isDraft && (
              <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                Unpublished
              </span>
            )}
            {canEdit && (
              <Button
                variant="ghost"
                size="icon-sm"
                className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(card.id);
                }}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            )}
          </div>
          <p className={cn("text-sm leading-relaxed text-foreground whitespace-pre-wrap", card.isDraft && !card.enabled ? "pr-[220px]" : card.isDraft || !card.enabled ? "pr-32" : "pr-12")}>
            {card.content}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <Textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter content..."
          className="min-h-[100px] resize-none"
          rows={4}
        />
      </CardContent>
      <CardFooter className="p-4 pt-0 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {canDelete && (
            <Button variant="destructive" size="sm" onClick={() => onDelete(card.id)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          <div className="flex items-center gap-2">
            <Switch
              checked={enabled}
              onCheckedChange={setEnabled}
              id={`enabled-${card.id}`}
              disabled={enabled && !canDisable}
              title={enabled && !canDisable ? "At least one card must be enabled in required sections" : ""}
            />
            <label
              htmlFor={`enabled-${card.id}`}
              className={cn("text-sm font-medium", enabled && !canDisable ? "cursor-not-allowed opacity-60" : "cursor-pointer")}
            >
              Enabled
            </label>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleCancel}>
            <X className="h-4 w-4" />
            Cancel
          </Button>
          <Button variant="default" size="sm" onClick={handleSave} disabled={!isValid}>
            <Save className="h-4 w-4" />
            Save
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};

export default SettingsCard;
